import { OptionProps } from '@shared/components/more-menu/MoreMenu';
import { useToast } from '@shared/components/toast';
import {
  ConversationEventType,
  useCreateAwkwardMomentLabelMutation,
  useCreateTranscriptionLabelMutation,
  useCreateTurnTakingLabelMutation,
  useTakeConversationSnapshotMutation,
} from '@shared/generated/graphql';
import {
  ConversationContributionRole,
  ConversationEvent,
  ConversationMessageEvent,
  ConversationMessageEventType,
  VoiceContribution,
} from '@shared/types/conversation';
import { assertUnreachable } from 'clerk_common/enum';
import { useState } from 'react';
import { NewDatasetInput } from '../save-snapshot-to-dataset/SaveSnapshotToDatasetModal';
import { useSaveSnapshotToDataset } from '../save-snapshot-to-dataset/useSaveSnapshotToDataset';
import {
  AwkwardMomentLabelRange,
  AwkwardMomentLabelSaveResult,
  AwkwardMomentLabelValue,
} from './AwkwardMomentLabelModal';

type TranscriptAdminConversation = {
  id: string;
  createdAt: Date;
  mediaUrl?: string;
  events?: ConversationEvent[];
};

type TranscriptAdminActionsParams = {
  conversation: TranscriptAdminConversation;
  isVoomaAdmin: boolean;
};

type TranscriptItemForOptions =
  | { type: 'message'; message: ConversationMessageEvent }
  | { type: 'conversationEvent' }
  | { type: 'event' };

type AwkwardMomentModalState = {
  voiceContribution: VoiceContribution;
  playbackSeconds: number;
};

type AwkwardMomentLabelMutationResult =
  | { rangeId: string; success: true }
  | { rangeId: string; success: false; error?: string };

type DatasetModalState = {
  timestamp: Date;
} | null;

export function useTranscriptAdminActions({
  conversation,
  isVoomaAdmin,
}: TranscriptAdminActionsParams) {
  const { sendToast } = useToast();
  const [takeConversationSnapshot] = useTakeConversationSnapshotMutation();
  const [createTranscriptionLabel] = useCreateTranscriptionLabelMutation();
  const [createTurnTakingLabel] = useCreateTurnTakingLabelMutation();
  const [createAwkwardMomentLabel] = useCreateAwkwardMomentLabelMutation();
  const { saveSnapshotToDataset } = useSaveSnapshotToDataset();

  const [datasetModalState, setDatasetModalState] =
    useState<DatasetModalState>(null);
  const [awkwardMomentModalState, setAwkwardMomentModalState] =
    useState<AwkwardMomentModalState | null>(null);

  const toastSnapshotFailure = () => {
    sendToast('Failed to take conversation snapshot', {
      variant: 'error',
    });
  };

  const takeSnapshotAtTime = async (time: Date) => {
    try {
      const res = await takeConversationSnapshot({
        variables: {
          input: {
            id: conversation.id,
            timestamp: time.toISOString(),
          },
        },
      });

      if (res.data?.takeConversationSnapshot.success) {
        sendToast('Conversation snapshot taken!', {
          variant: 'success',
        });
      } else {
        toastSnapshotFailure();
      }
    } catch {
      toastSnapshotFailure();
    }
  };

  const handleSaveSnapshotToDataset = async (
    existingDatasetIds: string[],
    newDatasets: NewDatasetInput[]
  ) => {
    if (!datasetModalState) return;
    try {
      await saveSnapshotToDataset({
        conversationId: conversation.id,
        timestamp: datasetModalState.timestamp,
        existingDatasetIds,
        newDatasets,
      });
      sendToast('Snapshot saved to dataset', { variant: 'success' });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to save snapshot to dataset';
      sendToast(message, { variant: 'error' });
      // NOTE(benjamin): Rethrow so the modal stays open and the user can retry.
      throw error;
    }
  };

  const handleCreateTranscriptionLabel = async (
    voiceContribution: VoiceContribution
  ) => {
    try {
      const res = await createTranscriptionLabel({
        variables: {
          input: {
            conversationId: conversation.id,
            contributionCreatedAt: voiceContribution.createdAt.toISOString(),
            contributionTranscript: voiceContribution.transcript,
            sourceMessageId: voiceContribution.id,
          },
        },
      });

      if (res.data?.createTranscriptionLabel.success) {
        sendToast(
          res.data.createTranscriptionLabel.alreadyFlagged
            ? 'Transcription label already exists'
            : 'Transcription label created',
          { variant: 'success' }
        );
      } else {
        sendToast(
          res.data?.createTranscriptionLabel.error ??
            'Failed to create transcription label',
          { variant: 'error' }
        );
      }
    } catch {
      sendToast('Failed to create transcription label', {
        variant: 'error',
      });
    }
  };

  const handleCreateTurnTakingLabel = async (
    voiceContribution: VoiceContribution
  ) => {
    try {
      const res = await createTurnTakingLabel({
        variables: {
          input: {
            conversationId: conversation.id,
            contributionId: voiceContribution.id,
          },
        },
      });

      if (res.data?.createTurnTakingLabel.success) {
        sendToast(
          res.data.createTurnTakingLabel.alreadyFlagged
            ? 'Turn-taking label already exists'
            : 'Turn-taking label created',
          { variant: 'success' }
        );
      } else {
        sendToast(
          res.data?.createTurnTakingLabel.error ??
            'Failed to create turn-taking label',
          { variant: 'error' }
        );
      }
    } catch {
      sendToast('Failed to create turn-taking label', {
        variant: 'error',
      });
    }
  };

  const openAwkwardMomentModal = (voiceContribution: VoiceContribution) => {
    const recordingStartTime =
      getRecordingStartTime(conversation.events) ?? conversation.createdAt;
    const contributionSeconds = secondsBetween(
      recordingStartTime,
      voiceContribution.createdAt
    );
    const playbackSeconds = Math.max(0, contributionSeconds);

    setAwkwardMomentModalState({
      voiceContribution,
      playbackSeconds,
    });
  };

  const handleCreateAwkwardMomentLabel = async (
    value: AwkwardMomentLabelValue
  ): Promise<AwkwardMomentLabelSaveResult | void> => {
    if (!awkwardMomentModalState) return;

    const results = await Promise.all(
      value.ranges.map(async (range) => {
        try {
          const res = await createAwkwardMomentLabel({
            variables: {
              input: {
                conversationId: conversation.id,
                startSeconds: range.startSeconds,
                endSeconds: range.endSeconds,
                awkwardMomentType: range.awkwardMomentType,
                note: range.note,
                sourceMessageId: awkwardMomentModalState.voiceContribution.id,
              },
            },
          });

          if (!res.data?.createAwkwardMomentLabel.success) {
            return makeFailedAwkwardMomentLabelResult(
              range,
              res.data?.createAwkwardMomentLabel.error
            );
          }

          return { rangeId: range.id, success: true as const };
        } catch (error) {
          return makeFailedAwkwardMomentLabelResult(
            range,
            error instanceof Error ? error.message : undefined
          );
        }
      })
    );

    const failedResults = results.filter(
      (
        result
      ): result is Extract<
        AwkwardMomentLabelMutationResult,
        { success: false }
      > => !result.success
    );
    const savedCount = results.length - failedResults.length;

    if (failedResults.length === 0) {
      sendToast(
        value.ranges.length === 1
          ? 'Awkward moment label created'
          : `${value.ranges.length} awkward moment labels created`,
        { variant: 'success' }
      );
      return;
    }

    if (savedCount > 0) {
      sendToast(
        `${savedCount} awkward moment ${
          savedCount === 1 ? 'label was' : 'labels were'
        } created. ${failedResults.length} failed and remain selected.`,
        { variant: 'error' }
      );
      return {
        failedRangeIds: failedResults.map((result) => result.rangeId),
      };
    }

    const message = failedResults[0]?.error ?? 'Failed to label awkward moment';
    sendToast(message, { variant: 'error' });
    throw new Error(message);
  };

  const makeOptions = (item: TranscriptItemForOptions) => {
    switch (item.type) {
      case 'message':
        return makeMessageOptions(item.message);
      case 'conversationEvent':
        return [];
      case 'event':
        return [];
      default:
        return assertUnreachable(item);
    }
  };

  const makeMessageOptions = (message: ConversationMessageEvent) => {
    if (!isVoomaAdmin) return [];
    const options: OptionProps[] = [
      {
        label: 'Take snapshot',
        onPress: () => takeSnapshotAtTime(message.createdAt),
      },
      {
        label: 'Save snapshot to dataset',
        onPress: () => setDatasetModalState({ timestamp: message.createdAt }),
      },
    ];

    if (message.type === ConversationMessageEventType.VOICE_CONTRIBUTION) {
      const voiceContribution = message as VoiceContribution;
      const isUserContribution =
        voiceContribution.role === ConversationContributionRole.USER;
      const callerOnlyTooltip = !isUserContribution
        ? 'Only available for caller turns'
        : undefined;

      options.push({
        label: 'Flag',
        isDisabled: !conversation.mediaUrl,
        tooltip: !conversation.mediaUrl
          ? 'Recording is not available for this conversation'
          : undefined,
        subOptions: [
          {
            label: 'Transcription',
            isDisabled: !isUserContribution,
            tooltip: callerOnlyTooltip,
            onPress: () => handleCreateTranscriptionLabel(voiceContribution),
          },
          {
            label: 'Turn taking',
            isDisabled: !isUserContribution,
            tooltip: callerOnlyTooltip,
            onPress: () => handleCreateTurnTakingLabel(voiceContribution),
          },
          {
            label: 'Other',
            onPress: () => openAwkwardMomentModal(voiceContribution),
          },
        ],
      });
    }

    return options;
  };

  return {
    awkwardMomentModalState,
    closeAwkwardMomentModal: () => setAwkwardMomentModalState(null),
    handleCreateAwkwardMomentLabel,
    datasetModalState,
    closeDatasetModal: () => setDatasetModalState(null),
    handleSaveSnapshotToDataset,
    makeOptions,
  };
}

function makeFailedAwkwardMomentLabelResult(
  range: AwkwardMomentLabelRange,
  error?: string | null
): AwkwardMomentLabelMutationResult {
  return {
    rangeId: range.id,
    success: false,
    error: error ?? 'Failed to label awkward moment',
  };
}

export function secondsBetween(start: Date, end: Date): number {
  return (end.getTime() - start.getTime()) / 1000;
}

export function getRecordingStartTime(
  events?: ConversationEvent[]
): Date | undefined {
  const recordingReceivedEvent = events
    ?.filter(
      (event) => event.name === ConversationEventType.CALL_RECORDING_RECEIVED
    )
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())[0];

  if (
    recordingReceivedEvent?.data?.eventDataType !==
    ConversationEventType.CALL_RECORDING_RECEIVED
  ) {
    return undefined;
  }

  const recordingStartTime = recordingReceivedEvent.data.recordingStartTime;
  return Number.isFinite(recordingStartTime.getTime())
    ? recordingStartTime
    : undefined;
}
